import { createAction, type PayloadAction, type Reducer } from '@reduxjs/toolkit';

export const undo = createAction('history/undo');
export const redo = createAction('history/redo');
export const clearHistory = createAction('history/clearHistory');

interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
}

interface UndoableOptions {
    filter?: (action: PayloadAction<any>) => boolean;
}

export default function undoable<T>(
  reducer: Reducer<T>,
  options: UndoableOptions = {}
): Reducer<HistoryState<T>> {
  const { filter } = options;
  const initialState: HistoryState<T> = {
    past: [],
    present: reducer(undefined, { type: '@@INIT' }),
    future: [],
  };

  const snapshot = (obj: T): T => JSON.parse(JSON.stringify(obj));

  return (state = initialState, action) => {
    const { past, present, future } = state;

    if (undo.match(action)) {
      if (past.length === 0) {
        return state;
      }
      const previous = past[past.length - 1];
      const newPast = past.slice(0, past.length - 1);
      return {
        past: newPast,
        present: previous,
        future: [snapshot(present), ...future],
      };
    }

    if (redo.match(action)) {
      if (future.length === 0) {
        return state;
      }
      const next = future[0];
      const newFuture = future.slice(1);
      return {
        past: [...past, snapshot(present)],
        present: next,
        future: newFuture,
      };
    }

    if (clearHistory.match(action)) {
      return {
        past: [],
        present,
        future: [],
      };
    }

    // Clone present before passing to the inner (Immer-powered) reducer
    // to ensure produce() receives a completely clean object with no
    // proxy metadata or frozen markers from previous Immer runs.
    const cleanInput = snapshot(present);
    const newPresent = reducer(cleanInput, action);

    if (cleanInput === newPresent) {
      return state;
    }

    const cleanPresent = snapshot(newPresent);

    if (filter && !filter(action as any)) {
        return {
            ...state,
            present: cleanPresent
        }
    }

    return {
      past: [...past, cleanInput],
      present: cleanPresent,
      future: [],
    };
  };
}
